// accordionHeaderBackground.js — pure model + CSS resolution + auto-contrast for
// the Accordion block's Title Background editor (Colors / Gradient / Patterns /
// Image · GIF). Bucket D / wave D2 / DL-037 (deck
// concepts/builder-blocks/09-accordion-header-bg-editor.html "N39" +
// concepts/builder-blocks/10-viewwheel-accordion-headers.html).
//
// REUSE, NOT DUPLICATION (feature-completeness law): the gradient / pattern /
// image / GIF CSS is delegated to the app's canonical `buildSegmentFillCss`
// (Shared/SegmentStyling/logic/segmentFill.js) — the SAME builder the Segment
// fills and the #1996 container backgrounds use. This module adds ONLY the
// accordion-specific model (the 4-tab subset the deck defines, Image tab hosts
// Image+GIF), the legacy-vs-fill RESOLUTION the renderer needs so a plain
// color-only header (every existing accordion, and the Colors tab) keeps its
// unchanged `headerBgColor` path and is byte-identical when the flag is OFF, and
// the auto-contrast title-tone rule (N39: the title flips white / navy by the
// header background's luminance — the Segment-icon luminance rule).
//
// PURE: no React, no DOM, no I/O — the risky mapping (a wrong string silently
// corrupts a user's header, a wrong tone makes a title unreadable) is unit-tested
// in isolation.

import { buildSegmentFillCss } from "../../Shared/SegmentStyling/logic/segmentFill";
import { defaultGradientState } from "../../../gradientBuilder/gradientCss";

/** Navy ink + white — the two title tones the Segment-icon rule flips between. */
export const TITLE_INK = "#123051";
export const TITLE_WHITE = "#FFFFFF";
// T22.542: AA fallback for mid-tone headers where neither navy nor white reaches
// 4.5:1 (black always does whenever white does not).
export const TITLE_BLACK = "#000000";

/**
 * The Accordion header background types, in the deck's selector order (09):
 * Colors · Gradient · Patterns · Image (Image tab hosts still Image + GIF via the
 * universal picker's `isGifAllowed`). "color" == the legacy solid-color path.
 */
export const HEADER_FILL_TYPES = ["color", "gradient", "pattern", "image"];

export const HEADER_FILL_TYPE_LABEL = {
  color: "Colors",
  gradient: "Gradient",
  pattern: "Patterns",
  image: "Image",
};

/** A fresh gradient fill seeded from the canonical default gradient state. */
export const defaultGradientFill = () => ({
  type: "gradient",
  gradient: defaultGradientState(),
});

/** A fresh, empty pattern fill (the picker fills in id/colors/css). */
export const defaultPatternFill = () => ({ type: "pattern", pattern: {} });

/** A fresh, empty image fill (renders the placeholder until a url is picked). */
export const defaultImageFill = () => ({ type: "image", image: {} });

/**
 * Coerce any stored value into a valid STRUCTURED header fill, or null.
 *
 * Returns null for undefined / non-object / the "color" type / anything invalid —
 * null means "use the legacy solid-color `headerBgColor` path" (existing
 * accordions and the Colors tab both take that path, so they stay byte-identical).
 */
export function normalizeHeaderFill(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.type === "gradient") {
    const gradient =
      raw.gradient && Array.isArray(raw.gradient.stops)
        ? raw.gradient
        : defaultGradientState();
    return { type: "gradient", gradient };
  }
  if (raw.type === "pattern") {
    const pattern = raw.pattern && typeof raw.pattern === "object" ? raw.pattern : {};
    return { type: "pattern", pattern };
  }
  if (raw.type === "image") {
    const image = raw.image && typeof raw.image === "object" ? raw.image : {};
    return { type: "image", image };
  }
  if (raw.type === "gif") {
    const gif = raw.gif && typeof raw.gif === "object" ? raw.gif : {};
    return { type: "gif", gif };
  }
  return null;
}

/** True when the stored fill is a structured (gradient/pattern/image/gif) fill. */
export function isStructuredFill(raw) {
  return normalizeHeaderFill(raw) !== null;
}

/**
 * The type the UI selector should show for a stored fill: the structured type
 * when present (gif collapses to the "image" tab), else "color".
 */
export function fillTypeOf(raw) {
  const fill = normalizeHeaderFill(raw);
  if (!fill) return "color";
  return fill.type === "gif" ? "image" : fill.type;
}

/**
 * The ONE entry point the renderer calls to decide how to paint the header
 * background.
 *
 *  - `{ mode: "legacy" }`  → use the accordion's existing solid-color
 *    `headerBgColor` path (unchanged; existing accordions, the Colors tab, or an
 *    invalid fill). This is what keeps the OFF path / plain headers byte-identical.
 *  - `{ mode: "fill", css }` → paint `css` (a gradient / pattern / image value)
 *    built by the canonical buildSegmentFillCss (patterns short-circuit to the
 *    BackgroundPatternPanel's own css when present).
 *
 * @param {*} raw stored `headerFill` node prop
 * @param {{open?:boolean, reducedMotion?:boolean}} [ctx] GIF playback context
 * @returns {{mode:'legacy'}|{mode:'fill', css:string}}
 */
export function resolveHeaderBackground(raw, ctx = {}) {
  const fill = normalizeHeaderFill(raw);
  if (!fill) return { mode: "legacy" };
  if (fill.type === "pattern" && fill.pattern && fill.pattern.css) {
    // BackgroundPatternPanel already produced a valid CSS background — the
    // canonical pattern producer — so use it verbatim.
    return { mode: "fill", css: fill.pattern.css };
  }
  return { mode: "fill", css: buildSegmentFillCss(fill, ctx) };
}

/* -------------------------------------------------------------------------- */
/* Auto-contrast — the Segment-icon luminance rule (N39)                       */
/* -------------------------------------------------------------------------- */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Parse #RGB / #RRGGBB into {r,g,b} 0..255, or null for anything else. */
export function parseHex(hex) {
  if (typeof hex !== "string") return null;
  const m = hex.trim().match(HEX_RE);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const int = parseInt(h, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/** WCAG relative luminance (0..1) for a hex color; null-safe (returns null). */
export function relativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** WCAG contrast ratio between two hex colors (>=1). null-safe (returns 0). */
export function contrastRatio(hexA, hexB) {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  if (la == null || lb == null) return 0;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Pick the readable title tone (white or navy ink) for a header background color
 * — the Segment-icon rule: whichever of white / navy has the higher contrast
 * against the background wins; black when neither reaches AA 4.5:1 (mid-tone
 * header, T22.542). Unknown/undeterminable backgrounds (image / GIF /
 * gradient with no single color) default to WHITE, preserving the app's existing
 * always-white header title (byte-identical when auto-contrast is not driving it).
 *
 * @param {string|null} bgHex representative background color, or null
 * @returns {string} TITLE_WHITE, TITLE_INK or TITLE_BLACK
 */
export function pickTitleTone(bgHex) {
  if (relativeLuminance(bgHex) == null) return TITLE_WHITE;
  const cWhite = contrastRatio(bgHex, TITLE_WHITE);
  const cInk = contrastRatio(bgHex, TITLE_INK);
  if (Math.max(cWhite, cInk) < 4.5) return TITLE_BLACK;
  return cWhite >= cInk ? TITLE_WHITE : TITLE_INK;
}

/**
 * The representative background color to run auto-contrast against, given the
 * stored fill + the legacy solid `headerBgColor`. Color → the legacy color;
 * gradient → its first stop; pattern → its background (bg) then foreground (fg);
 * image / GIF → null (cannot sample an arbitrary photo — defaults to white title).
 */
export function representativeBgColor(raw, legacyColor) {
  const fill = normalizeHeaderFill(raw);
  if (!fill) return legacyColor || null;
  if (fill.type === "gradient") {
    const stops = (fill.gradient && fill.gradient.stops) || [];
    return (stops[0] && stops[0].color) || null;
  }
  if (fill.type === "pattern") {
    const p = fill.pattern || {};
    return p.bg || p.fg || null;
  }
  return null; // image / gif — not sampleable
}

/**
 * The title color the renderer should apply given the header fill, the legacy
 * color, and whether auto-contrast is on. When auto-contrast is OFF (or a manual
 * `headerTextColor` is set), the caller keeps its existing color; this only
 * computes the auto tone.
 */
export function autoTitleTone(raw, legacyColor) {
  return pickTitleTone(representativeBgColor(raw, legacyColor));
}

/* -------------------------------------------------------------------------- */
/* "Apply to all Accordion headers in this Segment" propagation                */
/* -------------------------------------------------------------------------- */

/** The header-style keys propagated by "Apply to all Accordion headers". */
export const HEADER_STYLE_KEYS = [
  "headerBgColor",
  "headerOpacity",
  "headerFill",
  "headerTitleAutoContrast",
];

/**
 * The ids of the OTHER Accordion nodes in the craft tree (the current segment's
 * editor is per-segment, so every Accordion node in it is "in this Segment"),
 * given the craftjs `query.getNodes()` map. Pure so the sibling-selection — the
 * risky tree walk — is unit-tested without a running Editor.
 *
 * @param {object} nodes id -> craftjs Node (node.data.name is the resolver key)
 * @param {string} currentId the node being edited (excluded)
 * @param {string} [resolverName="Accordion"] the resolver key (pages/SegmentBuilder/Editor)
 * @returns {string[]}
 */
export function collectAccordionNodeIds(nodes, currentId, resolverName = "Accordion") {
  if (!nodes || typeof nodes !== "object") return [];
  return Object.keys(nodes).filter((id) => {
    if (id === currentId) return false;
    const n = nodes[id];
    const name = n && n.data && (n.data.name || n.data.displayName);
    return name === resolverName;
  });
}

const accordionHeaderBackground = {
  HEADER_FILL_TYPES,
  HEADER_STYLE_KEYS,
  collectAccordionNodeIds,
  HEADER_FILL_TYPE_LABEL,
  TITLE_INK,
  TITLE_WHITE,
  defaultGradientFill,
  defaultPatternFill,
  defaultImageFill,
  normalizeHeaderFill,
  isStructuredFill,
  fillTypeOf,
  resolveHeaderBackground,
  parseHex,
  relativeLuminance,
  contrastRatio,
  pickTitleTone,
  representativeBgColor,
  autoTitleTone,
};

export default accordionHeaderBackground;
